import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import { XIcon } from '@phosphor-icons/react';
import { VirtualizedAutocomplete } from '@repo/forms';
import { Card } from '@repo/ui';
import { useCallback, useMemo } from 'react';
import { type BooleanFilter, type Filter, FilterOption, FilterValue, genericMemo, type RangeFilter, RangeValue, type SelectFilter } from '.';

interface Props {
  filters: Filter[];
  onFilterChange: (newFilters: Record<string, FilterValue>) => void;
  toggleFilters: () => void;
  filterValues: Record<string, FilterValue>;
}

const SelectFilter = ({
  filterValue,
  filter,
  onFilterChange
}: {
  filterValue?: FilterOption[];
  filter: SelectFilter;
  onFilterChange: (filter: Filter, value: FilterOption[]) => void;
}) => (<>
  <Typography variant="subtitle2">{filter.label}</Typography>
  <VirtualizedAutocomplete
    multiple={filter.allowMultiple}
    options={filter.options}
    value={filter.allowMultiple
      ? (filterValue as FilterOption[] | undefined) ?? []
      : (Array.isArray(filterValue) ? (filterValue[0] ?? null) : null)
    }
    onChange={(_, value) => onFilterChange(filter, Array.isArray(value) ? value : value ? [value] : [])}
    placeholder={filter.label}
    listHeight={200}
    textFieldProps={{ size: "small" }}
    disableCloseOnSelect
    isCountrySelector={filter.isCountryField}
  />
</>
)

const RangeFilter = ({
  filterValue,
  filter,
  onFilterChange
}: {
  filterValue?: RangeValue;
  filter: RangeFilter;
  onFilterChange: (filter: Filter, value: RangeValue) => void;
}) => {
  const currentValue = filterValue || { min: undefined, max: undefined };

  const minOptions = useMemo(() => {
    if (currentValue.max !== undefined) {
      const maxIndex = filter.options.findIndex(opt => opt === currentValue.max);
      return filter.options.slice(0, maxIndex);
    }
    return filter.options;
  }, [filter.options, currentValue.max]);

  const maxOptions = useMemo(() => {
    if (currentValue.min !== undefined) {
      const minIndex = filter.options.findIndex(opt => opt === currentValue.min);
      return filter.options.slice(minIndex + 1);
    }
    return filter.options;
  }, [filter.options, currentValue.min]);

  const handleMinChange = useCallback((value: number | undefined) => {
    const newValue = { ...currentValue, min: value };
    // Clear max if it's now invalid
    if (newValue.max !== undefined && newValue.min !== undefined && newValue.min >= newValue.max) {
      newValue.max = undefined;
    }
    onFilterChange(filter, newValue);
  }, [currentValue, filter, onFilterChange]);

  const handleMaxChange = useCallback((value: number | undefined) => {
    const newValue = { ...currentValue, max: value };
    // Clear min if it's now invalid
    if (newValue.min !== undefined && newValue.max !== undefined && newValue.min >= newValue.max) {
      newValue.min = undefined;
    }
    onFilterChange(filter, newValue);
  }, [currentValue, filter, onFilterChange]);

  return (
    <>
      <Typography variant="subtitle2">{filter.label}</Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 80, flex: 1 }}>
          <InputLabel>Min</InputLabel>
          <Select
            value={currentValue.min ?? ''}
            label="Min"
            onChange={(e) => handleMinChange(e.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {minOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 80, flex: 1 }}>
          <InputLabel>Max</InputLabel>
          <Select
            value={currentValue.max ?? ''}
            label="Max"
            onChange={(e) => handleMaxChange(e.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {maxOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </>
  );
};

const BooleanFilter = ({
  filter,
  onFilterChange,
  filterValue
}: {
  filter: BooleanFilter;
  onFilterChange: (filter: Filter, value: boolean) => void;
  filterValue: boolean;
}) => (
  <FormControlLabel
    control={
      <Checkbox
        size="small"
        checked={filterValue}
        onChange={() => onFilterChange(filter, !filterValue)}
      />
    }
    label={filter.label}
  />
)

// FilterPanel Component
const FilterPanel = ({
  filters,
  onFilterChange,
  toggleFilters,
  filterValues,
}: Props) => {
  const handleFilterChange = useCallback((filter: Filter, value: FilterValue) => {
    const newFilterValues = {
      ...filterValues,
      [filter.dataKey]: value
    };
    onFilterChange(newFilterValues);
  }, [filterValues, onFilterChange]);

  const renderFilter = useCallback((filter: Filter, index: number) => {
    if (filter.type === 'select') {
      return (
        <SelectFilter
          key={index}
          filter={filter}
          filterValue={filterValues[filter.dataKey] as unknown as FilterOption[]}
          onFilterChange={handleFilterChange}
        />
      );
    } else if (filter.type === 'boolean') {
      return (
        <BooleanFilter
          key={index}
          filter={filter}
          filterValue={Boolean(filterValues[filter.dataKey])}
          onFilterChange={handleFilterChange}
        />
      );
    } else if (filter.type === 'range') {
      return (
        <RangeFilter
          key={index}
          filter={filter}
          filterValue={filterValues[filter.dataKey] as RangeValue}
          onFilterChange={handleFilterChange}
        />
      );
    }
    return null;
  }, [filterValues, handleFilterChange]);

  return (
    <Card
      sx={{
        flexShrink: 0,
        width: { md: 280 },
        position: { xs: 'absolute', md: 'relative' },
        zIndex: 10,
        top: 0,
        left: 0,
        height: '100%',
        maxHeight: { xs: '100dvh', md: '100%' },
        padding: 0
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" paddingTop={2} px={2}>
        <Typography variant="h6">🔍 Filters</Typography>
        <IconButton size="small" onClick={toggleFilters}>
          <XIcon size={20} />
        </IconButton>
      </Stack>
      <Divider />
      <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingX: 2, paddingBottom: 2 }}>
        {filters.filter(filter => filter.hidden !== true).map(renderFilter)}
      </Stack>
    </Card>
  );
};

export default genericMemo(FilterPanel);
